import {useCallback, useEffect, useMemo, useRef} from 'react';
import {Platform} from 'react-native';
import {useFocusEffect, useIsFocused} from '@react-navigation/native';
import {useQueryClient} from 'react-query';

import {
  getNotifications,
  markNotificationRead,
} from '../../server/react-query/useNotification';
import {markAsReadConsultation} from '../../server/react-query/useConsultation';
import {NotificationType} from '../../server/types/notification.types';
import useStores from '../../stores/useStores';
import {
  removeNotificationFromTray,
  removeDeliveredNotificationsByText,
  syncUnreadNotificationIndicators,
} from '../../utils/notification-badge.utils';

export type ConsultationNotificationStatusBucket =
  | 'pending'
  | 'ongoing'
  | 'history';

const notificationTypesByStatus: Record<
  ConsultationNotificationStatusBucket,
  Array<NotificationType | string | number>
> = {
  pending: [
    NotificationType.CONSULTATION_ACCEPTED,
    NotificationType.CONSULTATION_REQUEST,
    'ConsultationAccepted',
    'ConsultationRequest',
    2,
    0,
  ],
  ongoing: [
    NotificationType.MESSAGE_ADDED,
    NotificationType.CONSULTATION_PAID,
    NotificationType.SCHEDULED_CALL_REQUEST,
    NotificationType.APPOINTMENT_REQUEST,
    'MessageAdded',
    'ConsultationPaid',
    'ScheduledCallRequest',
    'AppointmentRequest',
    12,
    4,
    14,
  ],
  history: [
    NotificationType.CONSULTATION_CANCELLED,
    NotificationType.CONSULTATION_DECLINED,
    NotificationType.CONSULTATION_COMPLETED,
    NotificationType.CONSULTATION_ARCHIVED,
    'ConsultationCancelled',
    'ConsultationDeclined',
    'ConsultationCompleted',
    'ConsultationArchived',
    1,
    3,
    5,
    13,
  ],
};

const deliveredNotificationTextByStatus: Record<
  ConsultationNotificationStatusBucket,
  string[]
> = {
  pending: [
    'Approved consultation request for the patient',
    'New consultation request for the doctor',
    'consultation request',
    'new consultation request',
    'Notifications',
    'new notification',
  ],
  ongoing: [
    'New chat message for the patient',
    'New chat message for the doctor',
    'Payment made by patient for consultation',
    'Appointment Request',
    'You have an appointment',
    'chat message',
    'payment made',
    'appointment',
    'Notifications',
    'new notification',
  ],
  history: [
    'Consultation Cancelled',
    'Consultation Declined',
    'Consultation Completed',
    'Consultation Archived',
    'cancelled',
    'declined',
    'completed',
    'archived',
    'Notifications',
    'new notification',
  ],
};

const normalizeNotificationType = (type: NotificationType | string | number) =>
  String(type).trim().toLowerCase();

const parseNotificationContent = (content: any) => {
  if (!content || typeof content !== 'string') {
    return content;
  }

  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const getNotificationServiceRequestId = (content: any) => {
  const parsedContent = parseNotificationContent(content);

  return (
    parsedContent?.ServiceRequestId ||
    parsedContent?.serviceRequestId ||
    parsedContent?.ConsultationRequestId ||
    parsedContent?.consultationRequestId
  );
};

export const useMarkNotificationsReadByConsultationStatus = (
  statusBucket: ConsultationNotificationStatusBucket,
) => {
  const queryClient = useQueryClient();
  const isFocused = useIsFocused();
  const {authStore, patientProfileStore} = useStores();
  const isSyncingRef = useRef(false);
  const lastSyncAtRef = useRef(0);

  const targetTypes = useMemo(
    () =>
      new Set(
        notificationTypesByStatus[statusBucket].map(normalizeNotificationType),
      ),
    [statusBucket],
  );

  const subject =
    authStore.userRole === 'patient'
      ? patientProfileStore.activeProfileData?.patientId
      : undefined;

  const markNotificationsRead = useCallback(async () => {
    if (isSyncingRef.current) {
      return;
    }

    if (authStore.userRole === 'patient' && !subject) {
      return;
    }

    const now = Date.now();
    if (now - lastSyncAtRef.current < 2000) {
      return;
    }

    isSyncingRef.current = true;
    lastSyncAtRef.current = now;

    try {
      const notifications = await getNotifications({
        pageNumber: 1,
        pageSize: 1000,
        patientId: subject || '',
      });

      const unreadMatchingNotifications =
        notifications?.data?.filter(
          notification =>
            !notification.isRead &&
            targetTypes.has(normalizeNotificationType(notification.type)),
        ) ?? [];

      const hasMatchingNotifications = unreadMatchingNotifications.length > 0;

      if (hasMatchingNotifications) {
        await Promise.all(
          unreadMatchingNotifications.map(notification =>
            markNotificationRead({
              notificationId: notification.notificationId,
              subject,
            }),
          ),
        );

        if (
          authStore.userRole === 'practitioner' &&
          statusBucket === 'pending'
        ) {
          const serviceRequestIds = Array.from(
            new Set(
              unreadMatchingNotifications
                .map(notification =>
                  getNotificationServiceRequestId(notification.content),
                )
                .filter(Boolean)
                .map(String),
            ),
          );

          await Promise.all(
            serviceRequestIds.map(serviceRequestId =>
              markAsReadConsultation({serviceRequestId}),
            ),
          );
        }
      }

      if (Platform.OS !== 'web') {
        if (hasMatchingNotifications) {
          unreadMatchingNotifications.forEach(notification => {
            const serviceRequestId = getNotificationServiceRequestId(
              notification.content,
            );

            if (serviceRequestId) {
              removeNotificationFromTray(String(serviceRequestId));
            }
          });

          removeDeliveredNotificationsByText(
            deliveredNotificationTextByStatus[statusBucket],
          );
        }

        await syncUnreadNotificationIndicators(
          queryClient,
          subject,
          false,
        );
      }
    } catch (error) {
      console.log('Error marking status notifications as read:', error);
    } finally {
      isSyncingRef.current = false;
    }
  }, [authStore.userRole, queryClient, statusBucket, subject, targetTypes]);

  useFocusEffect(
    useCallback(() => {
      markNotificationsRead();
    }, [markNotificationsRead]),
  );

  useEffect(() => {
    if (isFocused) {
      markNotificationsRead();
    }
  }, [isFocused, markNotificationsRead]);

  return markNotificationsRead;
};
